export type LegalSection = {
  number?: string
  title: string
  intro?: string
  body?: string[]
  groups?: {
    heading: string
    items: string[]
  }[]
  outro?: string
}

export type LegalContent = {
  badge: string
  title: string
  updated?: string
  intro: string[]
  agreement?: string
  sections: LegalSection[]
  backToHome: string
}

export type LegalDocId =
  | 'offer'
  | 'refund'
  | 'delivery'
  | 'replacement'
  | 'dataDeletion'
  | 'terms'

const SUPPORT_EMAIL = 'admin@pickme.kz'
const SUPPORT_PHONE = '+7 701 034 64 43'

export const LEGAL_DOCUMENTS: Record<
  LegalDocId,
  { ru: LegalContent; kk: LegalContent; en?: LegalContent }
> = {
  // ─────────────────────────────────────────────────────────────────────────
  terms: {
    ru: {
      badge: 'Юридический документ',
      title: 'Условия использования Meelo',
      updated: 'Редакция от 23 сентября 2026 г.',
      backToHome: '← На главную',
      intro: [],
      sections: [
        {
          number: '1',
          title: 'Общие положения',
          body: [
            'Meelo — система управления детскими садами (веб-версия и мобильное приложение), которую разрабатывает и предоставляет ТОО «Мило Диджитал Солюшнс» (БИН 250540009597, г. Алматы). Настоящие условия регулируют использование сайта meelo.kz, веб-версии и мобильного приложения Meelo. Используя Meelo, вы соглашаетесь с этими условиями.',
            'Условия распространяются на:',
          ],
          groups: [
            {
              heading: '',
              items: [
                'клиентов — детские сады и сети, заключившие с нами договор;',
                'пользователей — сотрудников клиента и родителей, которым клиент предоставил доступ.',
              ],
            },
          ],
          outro:
            'Отношения с клиентом (стоимость, сроки, объём услуг) регулируются договором; при расхождении приоритет имеет договор.',
        },
        {
          number: '2',
          title: 'Доступ и учётные записи',
          body: [
            'Доступ в систему предоставляет клиент: он определяет, кто из сотрудников и родителей получает учётную запись и с какими правами. Пользователь обязан хранить данные для входа в тайне и не передавать доступ третьим лицам. О случаях несанкционированного доступа необходимо сообщить клиенту или нам.',
          ],
        },
        {
          number: '3',
          title: 'Правила использования',
          body: [
            'Meelo можно использовать только в целях, для которых система предназначена: учёт детей и посещаемости, начисления и оплаты, работа с сотрудниками, финансовый учёт организации.',
            'Запрещается:',
          ],
          groups: [
            {
              heading: '',
              items: [
                'вносить в систему заведомо недостоверные данные;',
                'получать доступ к данным других организаций или пользователей;',
                'обходить средства защиты, вмешиваться в работу системы, автоматически извлекать данные;',
                'копировать, изменять, декомпилировать программное обеспечение Meelo;',
                'использовать систему в нарушение законодательства Республики Казахстан.',
              ],
            },
          ],
        },
        {
          number: '4',
          title: 'Данные',
          body: [
            'Все данные, внесённые в систему, принадлежат клиенту. Клиент является оператором персональных данных детей, родителей и сотрудников и отвечает за законность их сбора, включая получение необходимых согласий. Meelo обрабатывает данные по поручению клиента и только для оказания услуг. Порядок удаления данных описан в Политике удаления данных.',
          ],
        },
        {
          number: '5',
          title: 'Интеллектуальная собственность',
          body: [
            'Программное обеспечение Meelo, его интерфейс, дизайн, товарный знак и документация принадлежат ТОО «Мило Диджитал Солюшнс». Клиенту предоставляется неисключительное право использования системы на срок действия договора. Права на программное обеспечение клиенту или пользователям не передаются.',
          ],
        },
        {
          number: '6',
          title: 'Работа системы и изменения',
          body: [
            'Мы обеспечиваем круглосуточную работу системы, за исключением плановых технических работ, о которых уведомляем заранее, и обстоятельств вне нашего контроля. Мы вправе развивать и изменять функционал системы; о существенных изменениях уведомляем клиентов.',
          ],
        },
        {
          number: '7',
          title: 'Ответственность',
          body: [
            'Meelo — инструмент учёта и автоматизации. Расчёты в системе выполняются на основании данных, внесённых клиентом; ответственность за полноту и корректность данных, а также за принятые на их основании решения несёт клиент. Наша ответственность перед клиентом ограничена суммой, уплаченной клиентом за услуги за последние 3 месяца, если иное не установлено договором или законом. Мы не несём ответственности за упущенную выгоду и косвенные убытки.',
          ],
        },
        {
          number: '8',
          title: 'Приостановка и прекращение доступа',
          body: [
            'Мы вправе приостановить доступ при нарушении настоящих условий или при угрозе безопасности системы, уведомив клиента. Доступ клиента прекращается в порядке, установленном договором; после прекращения договора данные удаляются в соответствии с Политикой удаления данных.',
          ],
        },
        {
          number: '9',
          title: 'Изменение условий',
          body: [
            'Мы можем изменять настоящие условия. Актуальная редакция публикуется на сайте meelo.kz с указанием даты. Продолжение использования системы после публикации означает согласие с новой редакцией.',
          ],
        },
        {
          number: '10',
          title: 'Применимое право',
          body: [
            'Настоящие условия регулируются законодательством Республики Казахстан. Споры разрешаются путём переговоров, а при недостижении согласия — в суде по месту нахождения ТОО «Мило Диджитал Солюшнс».',
          ],
        },
        {
          number: '11',
          title: 'Контакты',
          groups: [
            {
              heading: 'ТОО «Мило Диджитал Солюшнс»',
              items: [
                `Телефон: ${SUPPORT_PHONE}`,
                'Сайт: meelo.kz',
                `E-mail: ${SUPPORT_EMAIL}`,
              ],
            },
          ],
        },
      ],
    },
    kk: {
      badge: 'Заңды құжат',
      title: 'Meelo пайдалану шарттары',
      updated: '2026 жылғы 23 қыркүйектегі редакция',
      backToHome: '← Басты бетке',
      intro: [],
      sections: [
        {
          number: '1',
          title: 'Жалпы ережелер',
          body: [
            'Meelo — «Мило Диджитал Солюшнс» ЖШС (БСН 250540009597, Алматы қ.) әзірлейтін және ұсынатын балабақшаларды басқару жүйесі (веб-нұсқасы және мобильді қосымша). Осы шарттар meelo.kz сайтын, Meelo веб-нұсқасын және мобильді қосымшасын пайдалануды реттейді. Meelo-ны пайдалана отырып, сіз осы шарттармен келісесіз.',
            'Шарттар мыналарға қолданылады:',
          ],
          groups: [
            {
              heading: '',
              items: [
                'клиенттерге — бізбен шарт жасасқан балабақшалар мен желілерге;',
                'пайдаланушыларға — клиент қолжетімділік берген қызметкерлер мен ата-аналарға.',
              ],
            },
          ],
          outro:
            'Клиентпен қарым-қатынас (құны, мерзімдері, қызметтер көлемі) шартпен реттеледі; сәйкессіздік болған жағдайда шарт басым болады.',
        },
        {
          number: '2',
          title: 'Қолжетімділік және есептік жазбалар',
          body: [
            'Жүйеге қолжетімділікті клиент береді: қызметкерлер мен ата-аналардың қайсысы есептік жазба алатынын және қандай құқықтармен алатынын клиент анықтайды. Пайдаланушы кіру деректерін құпия сақтауға және қолжетімділікті үшінші тұлғаларға бермеуге міндетті. Рұқсатсыз кіру жағдайлары туралы клиентке немесе бізге хабарлау қажет.',
          ],
        },
        {
          number: '3',
          title: 'Пайдалану ережелері',
          body: [
            'Meelo-ны тек жүйе арналған мақсаттарда пайдалануға болады: балалар мен қатысуды есепке алу, есептеулер мен төлемдер, қызметкерлермен жұмыс, ұйымның қаржылық есебі.',
            'Тыйым салынады:',
          ],
          groups: [
            {
              heading: '',
              items: [
                'жүйеге көрінеу жалған деректерді енгізуге;',
                'басқа ұйымдардың немесе пайдаланушылардың деректеріне қол жеткізуге;',
                'қорғау құралдарын айналып өтуге, жүйе жұмысына араласуға, деректерді автоматты түрде алуға;',
                'Meelo бағдарламалық жасақтамасын көшіруге, өзгертуге, декомпиляциялауға;',
                'жүйені Қазақстан Республикасының заңнамасын бұза отырып пайдалануға.',
              ],
            },
          ],
        },
        {
          number: '4',
          title: 'Деректер',
          body: [
            'Жүйеге енгізілген барлық деректер клиентке тиесілі. Клиент балалардың, ата-аналардың және қызметкерлердің дербес деректерінің операторы болып табылады және оларды жинаудың заңдылығына, оның ішінде қажетті келісімдерді алуға жауап береді. Meelo деректерді клиенттің тапсырмасы бойынша және тек қызмет көрсету үшін өңдейді. Деректерді жою тәртібі Деректерді жою саясатында сипатталған.',
          ],
        },
        {
          number: '5',
          title: 'Зияткерлік меншік',
          body: [
            'Meelo бағдарламалық жасақтамасы, оның интерфейсі, дизайны, тауар белгісі және құжаттамасы «Мило Диджитал Солюшнс» ЖШС-ға тиесілі. Клиентке жүйені шарттың қолданылу мерзіміне пайдаланудың айрықша емес құқығы беріледі. Бағдарламалық жасақтамаға құқықтар клиентке немесе пайдаланушыларға берілмейді.',
          ],
        },
        {
          number: '6',
          title: 'Жүйенің жұмысы және өзгерістер',
          body: [
            'Біз жүйенің тәулік бойы жұмыс істеуін қамтамасыз етеміз; бұған алдын ала хабарланатын жоспарлы техникалық жұмыстар мен біздің бақылауымыздан тыс жағдайлар кірмейді. Біз жүйенің функционалын дамытуға және өзгертуге құқылымыз; елеулі өзгерістер туралы клиенттерге хабарлаймыз.',
          ],
        },
        {
          number: '7',
          title: 'Жауапкершілік',
          body: [
            'Meelo — есепке алу және автоматтандыру құралы. Жүйедегі есептеулер клиент енгізген деректер негізінде жүргізіледі; деректердің толықтығы мен дұрыстығына, сондай-ақ солардың негізінде қабылданған шешімдерге клиент жауап береді. Егер шартта немесе заңда өзгеше белгіленбесе, біздің клиент алдындағы жауапкершілігіміз клиенттің соңғы 3 айдағы қызметтер үшін төлеген сомасымен шектеледі. Біз жіберіп алған пайда мен жанама залалдар үшін жауап бермейміз.',
          ],
        },
        {
          number: '8',
          title: 'Қолжетімділікті тоқтата тұру және тоқтату',
          body: [
            'Осы шарттар бұзылған немесе жүйе қауіпсіздігіне қатер төнген жағдайда біз клиентке хабарлай отырып, қолжетімділікті тоқтата тұруға құқылымыз. Клиенттің қолжетімділігі шартта белгіленген тәртіппен тоқтатылады; шарт тоқтатылғаннан кейін деректер Деректерді жою саясатына сәйкес жойылады.',
          ],
        },
        {
          number: '9',
          title: 'Шарттарды өзгерту',
          body: [
            'Біз осы шарттарды өзгерте аламыз. Қолданыстағы редакция meelo.kz сайтында күні көрсетіліп жарияланады. Жарияланғаннан кейін жүйені пайдалануды жалғастыру жаңа редакциямен келісуді білдіреді.',
          ],
        },
        {
          number: '10',
          title: 'Қолданылатын құқық',
          body: [
            'Осы шарттар Қазақстан Республикасының заңнамасымен реттеледі. Даулар келіссөздер арқылы, ал келісімге қол жеткізілмеген жағдайда — «Мило Диджитал Солюшнс» ЖШС-ның орналасқан жері бойынша сотта шешіледі.',
          ],
        },
        {
          number: '11',
          title: 'Байланыс',
          groups: [
            {
              heading: '«Мило Диджитал Солюшнс» ЖШС',
              items: [
                `Телефон: ${SUPPORT_PHONE}`,
                'Сайт: meelo.kz',
                `E-mail: ${SUPPORT_EMAIL}`,
              ],
            },
          ],
        },
      ],
    },
    en: {
      badge: 'Legal document',
      title: 'Meelo Terms of Use',
      updated: 'Effective 23 September 2026',
      backToHome: '← Back to home',
      intro: [],
      sections: [
        {
          number: '1',
          title: 'General',
          body: [
            'Meelo is a kindergarten management system (web version and mobile app) developed and provided by Milo Digital Solutions LLP (BIN 250540009597, Almaty, Kazakhstan). These terms govern the use of the meelo.kz website, the Meelo web version and the mobile app. By using Meelo, you agree to these terms.',
            'The terms apply to:',
          ],
          groups: [
            {
              heading: '',
              items: [
                'clients — kindergartens and networks that have signed a contract with us;',
                "users — the client's staff and parents to whom the client has granted access.",
              ],
            },
          ],
          outro:
            'The relationship with a client (pricing, term, scope of services) is governed by the contract; in case of conflict, the contract prevails.',
        },
        {
          number: '2',
          title: 'Access and accounts',
          body: [
            'Access to the system is granted by the client, which decides which staff members and parents receive an account and with what permissions. Users must keep their login details confidential and must not share access with third parties. Any unauthorised access must be reported to the client or to us.',
          ],
        },
        {
          number: '3',
          title: 'Rules of use',
          body: [
            "Meelo may be used only for its intended purposes: managing children and attendance, charges and payments, staff, and the organisation's financial records.",
            'It is prohibited to:',
          ],
          groups: [
            {
              heading: '',
              items: [
                'enter knowingly false data into the system;',
                'access the data of other organisations or users;',
                "circumvent security measures, interfere with the system's operation, or extract data automatically;",
                'copy, modify or decompile the Meelo software;',
                'use the system in violation of the laws of the Republic of Kazakhstan.',
              ],
            },
          ],
        },
        {
          number: '4',
          title: 'Data',
          body: [
            "All data entered into the system belongs to the client. The client is the data controller for personal data of children, parents and staff and is responsible for the lawfulness of its collection, including obtaining the necessary consents. Meelo processes data on the client's instructions and solely to provide the services. Data deletion is described in the Data Deletion Policy.",
          ],
        },
        {
          number: '5',
          title: 'Intellectual property',
          body: [
            'The Meelo software, its interface, design, trademark and documentation belong to Milo Digital Solutions LLP. The client is granted a non-exclusive right to use the system for the term of the contract. No rights to the software are transferred to the client or users.',
          ],
        },
        {
          number: '6',
          title: 'Availability and changes',
          body: [
            "We keep the system available around the clock, except for scheduled maintenance, which we announce in advance, and circumstances beyond our control. We may develop and change the system's functionality; we notify clients of material changes.",
          ],
        },
        {
          number: '7',
          title: 'Liability',
          body: [
            'Meelo is a record-keeping and automation tool. Calculations in the system are based on the data entered by the client; the client is responsible for the completeness and accuracy of the data and for decisions made on its basis. Our liability to a client is limited to the amount paid by the client for the services over the preceding 3 months, unless the contract or the law provides otherwise. We are not liable for lost profits or indirect losses.',
          ],
        },
        {
          number: '8',
          title: 'Suspension and termination of access',
          body: [
            "We may suspend access, with notice to the client, in case of a breach of these terms or a threat to system security. A client's access is terminated as set out in the contract; after the contract ends, data is deleted in accordance with the Data Deletion Policy.",
          ],
        },
        {
          number: '9',
          title: 'Changes to these terms',
          body: [
            'We may amend these terms. The current version is published on meelo.kz with its effective date. Continued use of the system after publication constitutes acceptance of the new version.',
          ],
        },
        {
          number: '10',
          title: 'Governing law',
          body: [
            'These terms are governed by the laws of the Republic of Kazakhstan. Disputes are resolved by negotiation and, failing agreement, in court at the location of Milo Digital Solutions LLP.',
          ],
        },
        {
          number: '11',
          title: 'Contact',
          groups: [
            {
              heading: 'Milo Digital Solutions LLP',
              items: [
                `Phone: ${SUPPORT_PHONE}`,
                'Website: meelo.kz',
                `E-mail: ${SUPPORT_EMAIL}`,
              ],
            },
          ],
        },
      ],
    },
  },

  // ─────────────────────────────────────────────────────────────────────────
  dataDeletion: {
    ru: {
      badge: 'Юридический документ',
      title: 'Политика удаления данных',
      updated: 'Редакция от 23 сентября 2026 г.',
      backToHome: '← На главную',
      intro: [],
      sections: [
        {
          number: '1',
          title: 'О чём эта политика',
          body: [
            'Meelo — система управления детскими садами, которую разрабатывает и предоставляет ТОО «Мило Диджитал Солюшнс» (БИН 250540009597, г. Алматы). Настоящая политика описывает, какие данные мы удаляем после окончания сотрудничества с клиентом (детским садом или сетью), когда и как это происходит.',
          ],
        },
        {
          number: '2',
          title: 'Какие данные мы обрабатываем',
          body: [
            'В системе хранятся данные, которые клиент вносит для работы: сведения о детях и их родителях (ФИО, ИИН, контакты, группа, посещаемость, начисления и оплаты), данные сотрудников (ФИО, контакты, отметки посещаемости и геометки, расчёт заработной платы), а также настройки и финансовые записи организации. Клиент является собственником этих данных; Meelo обрабатывает их исключительно для оказания услуг по договору.',
          ],
        },
        {
          number: '3',
          title: 'Когда данные удаляются',
          body: [
            'Условия и сроки удаления закреплены в договоре с каждым клиентом. Удаление происходит автоматически после прекращения договора — клиенту не нужно направлять отдельный запрос или заявление. До даты прекращения договора клиент может запросить выгрузку своих данных.',
          ],
        },
        {
          number: '4',
          title: 'Как происходит удаление',
          intro: 'После прекращения договора:',
          groups: [
            {
              heading: '',
              items: [
                'все данные клиента, включая данные детей, родителей и сотрудников, удаляются из рабочей базы данных Meelo в срок, установленный договором;',
                'учётные записи пользователей клиента деактивируются, доступ к системе прекращается;',
                'резервные копии, содержащие данные клиента, перезаписываются в рамках стандартного цикла ротации — не позднее 30 дней после удаления из рабочей базы;',
                'после этого восстановление удалённых данных невозможно.',
              ],
            },
          ],
        },
        {
          number: '5',
          title: 'Что сохраняется',
          body: [
            'Мы сохраняем только документы, которые обязаны хранить по законодательству Республики Казахстан: договор, счета и акты. Они относятся к взаиморасчётам с клиентом как юридическим лицом и не содержат персональных данных детей, родителей и сотрудников.',
          ],
        },
        {
          number: '6',
          title: 'Обращения сотрудников и родителей',
          body: [
            'Оператором персональных данных детей, родителей и сотрудников является детский сад. Если вы хотите уточнить, изменить или удалить свои данные, обратитесь в свой детский сад — Meelo исполняет такие поручения клиента.',
          ],
        },
        {
          number: '7',
          title: 'Контакты',
          groups: [
            {
              heading: 'ТОО «Мило Диджитал Солюшнс»',
              items: [
                `Телефон: ${SUPPORT_PHONE}`,
                'Сайт: meelo.kz',
                `E-mail: ${SUPPORT_EMAIL}`,
              ],
            },
          ],
        },
      ],
    },
    kk: {
      badge: 'Заңды құжат',
      title: 'Деректерді жою саясаты',
      updated: '2026 жылғы 23 қыркүйектегі редакция',
      backToHome: '← Басты бетке',
      intro: [],
      sections: [
        {
          number: '1',
          title: 'Бұл саясат не туралы',
          body: [
            'Meelo — «Мило Диджитал Солюшнс» ЖШС (БСН 250540009597, Алматы қ.) әзірлейтін және ұсынатын балабақшаларды басқару жүйесі. Осы саясат клиентпен (балабақшамен немесе желімен) ынтымақтастық аяқталғаннан кейін қандай деректерді, қашан және қалай жоятынымызды сипаттайды.',
          ],
        },
        {
          number: '2',
          title: 'Біз қандай деректерді өңдейміз',
          body: [
            'Жүйеде клиент жұмыс үшін енгізетін деректер сақталады: балалар мен олардың ата-аналары туралы мәліметтер (аты-жөні, ЖСН, байланыс деректері, топ, қатысу, есептеулер мен төлемдер), қызметкерлердің деректері (аты-жөні, байланыс деректері, келу белгілері мен геобелгілер, жалақы есебі), сондай-ақ ұйымның баптаулары мен қаржылық жазбалары. Бұл деректердің иесі — клиент; Meelo оларды тек шарт бойынша қызмет көрсету үшін өңдейді.',
          ],
        },
        {
          number: '3',
          title: 'Деректер қашан жойылады',
          body: [
            'Жою шарттары мен мерзімдері әр клиентпен жасалған шартта бекітілген. Шарт тоқтатылғаннан кейін жою автоматты түрде жүргізіледі — клиенттің жеке сұраныс немесе өтініш жіберуі қажет емес. Шарт тоқтатылған күнге дейін клиент өз деректерінің көшірмесін сұрата алады.',
          ],
        },
        {
          number: '4',
          title: 'Жою қалай жүргізіледі',
          intro: 'Шарт тоқтатылғаннан кейін:',
          groups: [
            {
              heading: '',
              items: [
                'клиенттің барлық деректері, соның ішінде балалар, ата-аналар және қызметкерлер туралы деректер, шартта белгіленген мерзімде Meelo жұмыс дерекқорынан жойылады;',
                'клиент пайдаланушыларының есептік жазбалары өшіріледі, жүйеге қолжетімділік тоқтатылады;',
                'клиент деректері бар сақтық көшірмелер стандартты айналым циклі аясында қайта жазылады — жұмыс дерекқорынан жойылғаннан кейін 30 күннен кешіктірілмей;',
                'осыдан кейін жойылған деректерді қалпына келтіру мүмкін емес.',
              ],
            },
          ],
        },
        {
          number: '5',
          title: 'Не сақталады',
          body: [
            'Біз Қазақстан Республикасының заңнамасы бойынша сақтауға міндетті құжаттарды ғана сақтаймыз: шарт, шоттар және актілер. Олар заңды тұлға ретіндегі клиентпен өзара есеп айырысуға қатысты және балалардың, ата-аналардың және қызметкерлердің дербес деректерін қамтымайды.',
          ],
        },
        {
          number: '6',
          title: 'Қызметкерлер мен ата-аналардың өтініштері',
          body: [
            'Балалардың, ата-аналардың және қызметкерлердің дербес деректерінің операторы — балабақша. Өз деректеріңізді нақтылағыңыз, өзгерткіңіз немесе жойғыңыз келсе, өз балабақшаңызға хабарласыңыз — Meelo клиенттің мұндай тапсырмаларын орындайды.',
          ],
        },
        {
          number: '7',
          title: 'Байланыс',
          groups: [
            {
              heading: '«Мило Диджитал Солюшнс» ЖШС',
              items: [
                `Телефон: ${SUPPORT_PHONE}`,
                'Сайт: meelo.kz',
                `E-mail: ${SUPPORT_EMAIL}`,
              ],
            },
          ],
        },
      ],
    },
    en: {
      badge: 'Legal document',
      title: 'Data Deletion Policy',
      updated: 'Effective 23 September 2026',
      backToHome: '← Back to home',
      intro: [],
      sections: [
        {
          number: '1',
          title: 'Scope',
          body: [
            'Meelo is a kindergarten management system developed and provided by Milo Digital Solutions LLP (BIN 250540009597, Almaty, Kazakhstan). This policy describes what data we delete once our cooperation with a client (a kindergarten or a network) ends, and when and how this happens.',
          ],
        },
        {
          number: '2',
          title: 'Data we process',
          body: [
            "The system stores the data a client enters to run its operations: information about children and their parents (full name, IIN, contact details, group, attendance, charges and payments), staff data (full name, contact details, check-in and geo-location marks, payroll calculations), and the organisation's settings and financial records. The client owns this data; Meelo processes it solely to provide the services under the contract.",
          ],
        },
        {
          number: '3',
          title: 'When data is deleted',
          body: [
            'Deletion terms and timelines are set out in the contract with each client. Deletion takes place automatically after the contract ends — the client does not need to submit a separate request. Before the termination date, the client may request an export of its data.',
          ],
        },
        {
          number: '4',
          title: 'How deletion works',
          intro: 'After the contract ends:',
          groups: [
            {
              heading: '',
              items: [
                "all client data, including data on children, parents and staff, is deleted from Meelo's production database within the period defined in the contract;",
                "the client's user accounts are deactivated and access to the system is terminated;",
                'backups containing client data are overwritten under the standard rotation cycle — no later than 30 days after deletion from the production database;',
                'after that, deleted data cannot be restored.',
              ],
            },
          ],
        },
        {
          number: '5',
          title: 'What we retain',
          body: [
            'We retain only the documents we are required to keep under the laws of the Republic of Kazakhstan: the contract, invoices and acts of acceptance. These relate to settlements with the client as a legal entity and do not contain personal data of children, parents or staff.',
          ],
        },
        {
          number: '6',
          title: 'Requests from staff and parents',
          body: [
            'The kindergarten is the data controller for personal data of children, parents and staff. If you wish to review, correct or delete your data, please contact your kindergarten — Meelo carries out such instructions from the client.',
          ],
        },
        {
          number: '7',
          title: 'Contact',
          groups: [
            {
              heading: 'Milo Digital Solutions LLP',
              items: [
                `Phone: ${SUPPORT_PHONE}`,
                'Website: meelo.kz',
                `E-mail: ${SUPPORT_EMAIL}`,
              ],
            },
          ],
        },
      ],
    },
  },

  // ─────────────────────────────────────────────────────────────────────────
  offer: {
    ru: {
      badge: 'Юридический документ',
      title:
        'Договор публичной оферты на оказание услуг доступа к платформе «meelo»',
      backToHome: '← На главную',
      intro: [
        'Настоящий документ является официальным публичным предложением ТОО «Мило Диджитал Солюшнс» (БИН 250540009597) заключить договор на оказание услуг по предоставлению доступа к облачной программной платформе для управления дошкольной организацией «meelo».',
      ],
      agreement:
        'Совершая оплату и/или регистрируясь на Платформе, Держатель Карточки и Заказчик подтверждают, что полностью ознакомлены и безоговорочно согласны с условиями настоящей Оферты.',
      sections: [
        {
          number: '1',
          title: 'Общие положения',
          body: [
            '1.1. Настоящий документ является официальным публичным предложением (далее — «Оферта») Товарищества с ограниченной ответственностью «Мило Диджитал Солюшнс» (БИН 250540009597) (далее — «Предприятие» или «Исполнитель») заключить договор на оказание услуг по предоставлению доступа к облачной программной платформе для управления дошкольной организацией «meelo» (далее — «Платформа», «Услуги») на изложенных ниже условиях.',
            '1.2. В соответствии со статьями 395, 396 и 447 Гражданского кодекса Республики Казахстан настоящая Оферта является публичной. Договор, заключаемый на основании её акцепта, является договором присоединения (статья 389 Гражданского кодекса Республики Казахстан), условия которого принимаются Заказчиком не иначе как путём присоединения к Оферте в целом.',
            '1.3. Оферта может быть акцептована любым дееспособным физическим или юридическим лицом (дошкольной организацией, частным детским садом, индивидуальным предпринимателем и т.п.), имеющим намерение приобрести доступ к Платформе на территории Республики Казахстан.',
            '1.4. Оплата Услуг производится с использованием платёжной карты. Совершая оплату и/или регистрируясь на Платформе, Держатель Карточки и Заказчик подтверждают, что полностью ознакомлены и безоговорочно согласны с условиями настоящей Оферты, а также с Политикой конфиденциальности, Порядком возврата, Порядком предоставления доступа и Порядком замены, размещёнными на сайте meelo.kz.',
          ],
        },
        {
          number: '2',
          title: 'Термины и определения',
          body: [
            '2.1. Предприятие (Исполнитель) — ТОО «Мило Диджитал Солюшнс».',
            '2.2. Платформа / Услуги / «Товар» — облачное программное обеспечение «meelo», предназначенное для автоматизации управления деятельностью дошкольной организации (учёт воспитанников и групп, посещаемость, расписание, начисления и оплаты, коммуникация с законными представителями и иные функции), доступ к которому предоставляется по подписке. Для целей настоящей Оферты и требований платёжных систем термин «Товар» означает Услуги доступа к Платформе; материальный носитель не передаётся.',
            '2.3. Заказчик (Пользователь) — лицо, акцептовавшее Оферту и использующее Платформу.',
            '2.4. Держатель Карточки — физическое лицо, на чьё имя выпущена платёжная карта, либо лицо, уполномоченное произвести оплату Услуг с использованием платёжной карты.',
            '2.5. Акцепт — полное и безоговорочное принятие Оферты путём оплаты Услуг и/или регистрации учётной записи на Платформе.',
            '2.6. Подписка (Тариф) — выбранный Заказчиком объём функций и период, в течение которого предоставляется доступ к Платформе.',
          ],
        },
        {
          number: '3',
          title: 'Предмет договора',
          body: [
            '3.1. Исполнитель обязуется предоставить Заказчику доступ к Платформе (неисключительное право использования программного обеспечения) в объёме выбранного Тарифа, а Заказчик обязуется оплатить Услуги в порядке и сроки, установленные настоящей Офертой.',
            '3.2. Услуги оказываются дистанционно по модели «программное обеспечение как услуга» (SaaS); доступ предоставляется через сеть Интернет.',
            '3.3. Перечень функций, состав Тарифов и их стоимость размещаются на сайте meelo.kz. Информация о Платформе, предоставляемая на сайте, признаётся полной и достоверной в соответствии с законодательством Республики Казахстан о защите прав потребителей и о регулировании торговой деятельности.',
          ],
        },
        {
          number: '4',
          title: 'Порядок заключения договора (акцепт)',
          body: [
            '4.1. Договор считается заключённым с момента совершения Акцепта — оплаты Услуг и/или регистрации учётной записи на Платформе.',
            '4.2. Акцепт Оферты означает, что Заказчик ознакомлен с функциональными возможностями, Тарифами и стоимостью Услуг, и они ему понятны.',
          ],
        },
        {
          number: '5',
          title: 'Стоимость Услуг и порядок оплаты',
          body: [
            '5.1. Стоимость Услуг определяется выбранным Тарифом, указывается на сайте meelo.kz в тенге и включает все налоги и обязательные платежи, предусмотренные законодательством Республики Казахстан.',
            '5.2. Оплата производится в безналичном порядке с использованием платёжной карты через систему интернет-эквайринга банка. Безопасность платежей и обработка данных платёжных карт обеспечиваются банком-эквайером и платёжными системами; Предприятие не получает и не хранит полные реквизиты платёжных карт.',
            '5.3. Услуги предоставляются на условиях предоплаты, если иное прямо не указано в Тарифе.',
            '5.4. Исполнитель вправе изменять Тарифы и стоимость в одностороннем порядке с публикацией на сайте. К уже оплаченному периоду применяются условия и стоимость, действовавшие на момент оплаты.',
          ],
        },
        {
          number: '6',
          title: 'Права и обязанности сторон',
          body: [
            '6.1. Исполнитель обязуется: предоставить доступ к Платформе в объёме Тарифа; обеспечивать работоспособность Платформы и техническую поддержку; уведомлять о плановых технических работах; обрабатывать данные в соответствии с законодательством Республики Казахстан.',
            '6.2. Исполнитель вправе: приостановить доступ при нарушении Заказчиком условий Оферты или при неоплате; привлекать третьих лиц для оказания Услуг; вносить изменения и улучшения в Платформу.',
            '6.3. Заказчик обязуется: своевременно оплачивать Услуги; предоставлять достоверные данные; не передавать данные доступа третьим лицам; использовать Платформу в соответствии с её назначением и законодательством; обеспечить наличие правовых оснований (согласий законных представителей) для размещения на Платформе персональных данных воспитанников и иных лиц.',
            '6.4. Заказчик вправе: получать доступ к оплаченным Услугам и технической поддержке; отказаться от Услуг и расторгнуть договор в порядке, предусмотренном настоящей Офертой и Порядком возврата.',
          ],
        },
        {
          number: '7',
          title: 'Персональные данные и конфиденциальность',
          body: [
            '7.1. Обработка персональных данных осуществляется в соответствии с Законом Республики Казахстан «О персональных данных и их защите» от 21 мая 2013 года № 94-V и Законом Республики Казахстан «Об информатизации».',
            '7.2. Размещая на Платформе персональные данные воспитанников, их законных представителей и сотрудников, Заказчик выступает владельцем (собственником) таких данных. Исполнитель обрабатывает указанные данные исключительно по поручению и в интересах Заказчика в целях оказания Услуг. Получение согласия законных представителей несовершеннолетних на обработку их персональных данных обеспечивает Заказчик.',
            '7.3. Стороны обязуются сохранять конфиденциальность данных и не передавать их третьим лицам, кроме случаев, предусмотренных законодательством Республики Казахстан.',
          ],
        },
        {
          number: '8',
          title: 'Ответственность сторон',
          body: [
            '8.1. За неисполнение или ненадлежащее исполнение обязательств стороны несут ответственность в соответствии с законодательством Республики Казахстан.',
            '8.2. Исполнитель не несёт ответственности за сбои, вызванные действиями Заказчика, третьих лиц, провайдеров связи, а также обстоятельствами непреодолимой силы.',
            '8.3. Совокупная ответственность Исполнителя ограничивается стоимостью Услуг, оплаченных Заказчиком за период, в котором возникло нарушение, если иное не предусмотрено законодательством Республики Казахстан о защите прав потребителей.',
          ],
        },
        {
          number: '9',
          title: 'Возврат средств и расторжение договора',
          body: [
            '9.1. Порядок возврата Услуг и денежных средств определяется отдельным документом «Порядок возврата», размещённым на сайте meelo.kz и являющимся неотъемлемой частью настоящей Оферты.',
            '9.2. Каждая из сторон вправе расторгнуть договор в порядке, предусмотренном законодательством Республики Казахстан и настоящей Офертой.',
          ],
        },
        {
          number: '10',
          title: 'Форс-мажор',
          body: [
            '10.1. Стороны освобождаются от ответственности за неисполнение обязательств, если оно вызвано обстоятельствами непреодолимой силы (стихийные бедствия, аварии, действия государственных органов, массовые сбои сетей и иные обстоятельства вне разумного контроля сторон).',
          ],
        },
        {
          number: '11',
          title: 'Срок действия и изменение Оферты',
          body: [
            '11.1. Оферта вступает в силу с момента размещения на сайте meelo.kz и действует до её отзыва или замены новой редакцией.',
            '11.2. Исполнитель вправе в одностороннем порядке изменять Оферту с публикацией новой редакции на сайте. Изменения вступают в силу с момента публикации.',
            '11.3. Во всём, что не урегулировано Офертой, стороны руководствуются законодательством Республики Казахстан.',
          ],
        },
      ],
    },
    kk: {
      badge: 'Заңды құжат',
      title:
        '«meelo» платформасына қол жеткізу қызметтерін көрсету туралы жария оферта шарты',
      backToHome: '← Басты бетке',
      intro: [
        'Осы құжат «Мило Диджитал Солюшнс» жауапкершілігі шектеулі серіктестігінің (БСН 250540009597) мектепке дейінгі ұйымды басқаруға арналған «meelo» бұлттық бағдарламалық платформасына қол жеткізу қызметтерін көрсету туралы шарт жасасу жөніндегі ресми жария ұсынысы болып табылады.',
      ],
      agreement:
        'Ақы төлей отырып және/немесе Платформада тіркеле отырып, Карта ұстаушы мен Тапсырыс беруші осы Офертаның талаптарымен толық танысқанын және оларға сөзсіз келісетінін растайды.',
      sections: [
        {
          number: '1',
          title: 'Жалпы ережелер',
          body: [
            '1.1. Осы құжат «Мило Диджитал Солюшнс» жауапкершілігі шектеулі серіктестігінің (БСН 250540009597) (бұдан әрі — «Кәсіпорын» немесе «Орындаушы») мектепке дейінгі ұйымды басқаруға арналған «meelo» бұлттық бағдарламалық платформасына (бұдан әрі — «Платформа», «Қызметтер») қол жеткізу қызметтерін көрсету туралы шарт жасасу жөніндегі ресми жария ұсынысы (бұдан әрі — «Оферта») болып табылады.',
            '1.2. Қазақстан Республикасы Азаматтық кодексінің 395, 396 және 447-баптарына сәйкес осы Оферта жария болып табылады. Оны акцептеу негізінде жасалатын шарт қосылу шарты (Қазақстан Республикасы Азаматтық кодексінің 389-бабы) болып табылады, оның талаптарын Тапсырыс беруші Офертаға тұтастай қосылу жолымен ғана қабылдайды.',
            '1.3. Офертаны Қазақстан Республикасының аумағында Платформаға қол жеткізуді сатып алуға ниеті бар кез келген әрекетке қабілетті жеке немесе заңды тұлға (мектепке дейінгі ұйым, жеке балабақша, дара кәсіпкер және т.б.) акцептей алады.',
            '1.4. Қызметтерге ақы төлеу төлем картасын пайдалану арқылы жүргізіледі. Ақы төлей отырып және/немесе Платформада тіркеле отырып, Карта ұстаушы мен Тапсырыс беруші осы Офертаның, сондай-ақ meelo.kz сайтында орналастырылған Құпиялылық саясатының, Қайтару тәртібінің, Қол жеткізуді ұсыну тәртібінің және Ауыстыру тәртібінің талаптарымен толық танысқанын және оларға сөзсіз келісетінін растайды.',
          ],
        },
        {
          number: '2',
          title: 'Терминдер мен анықтамалар',
          body: [
            '2.1. Кәсіпорын (Орындаушы) — «Мило Диджитал Солюшнс» ЖШС.',
            '2.2. Платформа / Қызметтер / «Тауар» — мектепке дейінгі ұйымның қызметін автоматтандыруға арналған (тәрбиеленушілер мен топтарды есепке алу, келу-кетуді бақылау, кесте, есептеулер мен төлемдер, заңды өкілдермен байланыс және өзге де функциялар) «meelo» бұлттық бағдарламалық қамтылымы; оған қол жеткізу жазылым бойынша беріледі. Осы Оферта және төлем жүйелерінің талаптары мақсатында «Тауар» термині Платформаға қол жеткізу Қызметтерін білдіреді; материалдық жеткізгіш берілмейді.',
            '2.3. Тапсырыс беруші (Пайдаланушы) — Офертаны акцептеген және Платформаны пайдаланатын тұлға.',
            '2.4. Карта ұстаушы — атына төлем картасы шығарылған жеке тұлға не Қызметтерге төлем картасымен ақы төлеуге уәкілетті тұлға.',
            '2.5. Акцепт — Қызметтерге ақы төлеу және/немесе Платформада тіркелу жолымен Офертаны толық әрі сөзсіз қабылдау.',
            '2.6. Жазылым (Тариф) — Тапсырыс беруші таңдаған функциялар көлемі және Платформаға қол жеткізу берілетін мерзім.',
          ],
        },
        {
          number: '3',
          title: 'Шарттың мәні',
          body: [
            '3.1. Орындаушы Тапсырыс берушіге таңдалған Тариф көлемінде Платформаға қол жеткізуді (бағдарламалық қамтылымды пайдаланудың айрықша емес құқығын) беруге, ал Тапсырыс беруші Қызметтерге осы Офертада белгіленген тәртіппен және мерзімде ақы төлеуге міндеттенеді.',
            '3.2. Қызметтер «қызмет ретіндегі бағдарламалық қамтылым» (SaaS) моделі бойынша қашықтан көрсетіледі; қол жеткізу Интернет желісі арқылы беріледі.',
            '3.3. Функциялар тізбесі, Тарифтердің құрамы мен құны meelo.kz сайтында орналастырылады. Сайтта берілетін Платформа туралы ақпарат Қазақстан Республикасының тұтынушылардың құқықтарын қорғау және сауда қызметін реттеу туралы заңнамасына сәйкес толық әрі дұрыс деп танылады.',
          ],
        },
        {
          number: '4',
          title: 'Шарт жасасу тәртібі (акцепт)',
          body: [
            '4.1. Шарт Акцепт жасалған сәттен — Қызметтерге ақы төленген және/немесе Платформада есептік жазба тіркелген сәттен бастап жасалды деп есептеледі.',
            '4.2. Офертаны акцептеу Тапсырыс берушінің Платформаның функционалдық мүмкіндіктерімен, Тарифтермен және Қызметтердің құнымен танысқанын әрі олар оған түсінікті екенін білдіреді.',
          ],
        },
        {
          number: '5',
          title: 'Қызметтердің құны және төлеу тәртібі',
          body: [
            '5.1. Қызметтердің құны таңдалған Тарифпен айқындалады, meelo.kz сайтында теңгемен көрсетіледі және Қазақстан Республикасының заңнамасында көзделген барлық салықтар мен міндетті төлемдерді қамтиды.',
            '5.2. Төлем банктің интернет-эквайринг жүйесі арқылы төлем картасын пайдалана отырып қолма-қол ақшасыз нысанда жүргізіледі. Төлемдердің қауіпсіздігін және төлем карталары деректерін өңдеуді эквайер-банк пен төлем жүйелері қамтамасыз етеді; Кәсіпорын төлем карталарының толық деректемелерін алмайды және сақтамайды.',
            '5.3. Тарифте өзгеше тікелей көрсетілмесе, Қызметтер алдын ала төлеу шартымен көрсетіледі.',
            '5.4. Орындаушы Тарифтер мен құнды сайтта жариялай отырып біржақты тәртіппен өзгертуге құқылы. Бұрын төленген кезеңге төлем жасалған сәтте қолданыста болған талаптар мен құн қолданылады.',
          ],
        },
        {
          number: '6',
          title: 'Тараптардың құқықтары мен міндеттері',
          body: [
            '6.1. Орындаушы: Тариф көлемінде Платформаға қол жеткізуді беруге; Платформаның жұмысқа қабілеттілігін және техникалық қолдауды қамтамасыз етуге; жоспарлы техникалық жұмыстар туралы хабарлауға; деректерді Қазақстан Республикасының заңнамасына сәйкес өңдеуге міндеттенеді.',
            '6.2. Орындаушы: Тапсырыс беруші Оферта талаптарын бұзған немесе ақы төлемеген жағдайда қол жеткізуді тоқтата тұруға; Қызметтерді көрсету үшін үшінші тұлғаларды тартуға; Платформаға өзгерістер мен жақсартулар енгізуге құқылы.',
            '6.3. Тапсырыс беруші: Қызметтерге уақтылы ақы төлеуге; дұрыс деректер беруге; қол жеткізу деректерін үшінші тұлғаларға бермеуге; Платформаны оның мақсатына және заңнамаға сәйкес пайдалануға; Платформада тәрбиеленушілердің және өзге де тұлғалардың дербес деректерін орналастыру үшін құқықтық негіздердің (заңды өкілдердің келісімдерінің) болуын қамтамасыз етуге міндеттенеді.',
            '6.4. Тапсырыс беруші: төленген Қызметтерге және техникалық қолдауға қол жеткізуге; осы Офертада және Қайтару тәртібінде көзделген тәртіппен Қызметтерден бас тартуға және шартты бұзуға құқылы.',
          ],
        },
        {
          number: '7',
          title: 'Дербес деректер және құпиялылық',
          body: [
            '7.1. Дербес деректерді өңдеу Қазақстан Республикасының «Дербес деректер және оларды қорғау туралы» 2013 жылғы 21 мамырдағы № 94-V Заңына және «Ақпараттандыру туралы» Заңына сәйкес жүзеге асырылады.',
            '7.2. Платформада тәрбиеленушілердің, олардың заңды өкілдерінің және қызметкерлердің дербес деректерін орналастыра отырып, Тапсырыс беруші осындай деректердің иесі (меншік иесі) болып табылады. Орындаушы аталған деректерді тек Тапсырыс берушінің тапсырмасы бойынша және оның мүддесінде Қызметтерді көрсету мақсатында өңдейді. Кәмелетке толмағандардың дербес деректерін өңдеуге заңды өкілдердің келісімін алуды Тапсырыс беруші қамтамасыз етеді.',
            '7.3. Тараптар деректердің құпиялылығын сақтауға және оларды Қазақстан Республикасының заңнамасында көзделген жағдайлардан басқа кезде үшінші тұлғаларға бермеуге міндеттенеді.',
          ],
        },
        {
          number: '8',
          title: 'Тараптардың жауапкершілігі',
          body: [
            '8.1. Міндеттемелерді орындамағаны немесе тиісінше орындамағаны үшін тараптар Қазақстан Республикасының заңнамасына сәйкес жауапты болады.',
            '8.2. Орындаушы Тапсырыс берушінің, үшінші тұлғалардың, байланыс провайдерлерінің әрекеттерінен, сондай-ақ еңсерілмейтін күш мән-жайларынан туындаған іркілістер үшін жауапты болмайды.',
            '8.3. Орындаушының жиынтық жауапкершілігі, егер Қазақстан Республикасының тұтынушылардың құқықтарын қорғау туралы заңнамасында өзгеше көзделмесе, бұзушылық туындаған кезеңде Тапсырыс беруші төлеген Қызметтердің құнымен шектеледі.',
          ],
        },
        {
          number: '9',
          title: 'Ақшаны қайтару және шартты бұзу',
          body: [
            '9.1. Қызметтер мен ақшалай қаражатты қайтару тәртібі meelo.kz сайтында орналастырылған және осы Офертаның ажырамас бөлігі болып табылатын «Қайтару тәртібі» жеке құжатымен айқындалады.',
            '9.2. Тараптардың әрқайсысы Қазақстан Республикасының заңнамасында және осы Офертада көзделген тәртіппен шартты бұзуға құқылы.',
          ],
        },
        {
          number: '10',
          title: 'Еңсерілмейтін күш (форс-мажор)',
          body: [
            '10.1. Тараптар міндеттемелерді орындамауы еңсерілмейтін күш мән-жайларынан (дүлей зілзалалар, апаттар, мемлекеттік органдардың әрекеттері, желілердің жаппай іркілісі және тараптардың ақылға қонымды бақылауынан тыс өзге де мән-жайлар) туындаған жағдайда жауапкершіліктен босатылады.',
          ],
        },
        {
          number: '11',
          title: 'Офертаның қолданылу мерзімі және оны өзгерту',
          body: [
            '11.1. Оферта meelo.kz сайтында орналастырылған сәттен бастап күшіне енеді және ол кері қайтарылғанға немесе жаңа редакциямен ауыстырылғанға дейін қолданылады.',
            '11.2. Орындаушы жаңа редакцияны сайтта жариялай отырып, Офертаны біржақты тәртіппен өзгертуге құқылы. Өзгерістер жарияланған сәттен бастап күшіне енеді.',
            '11.3. Офертада реттелмеген барлық мәселеде тараптар Қазақстан Республикасының заңнамасын басшылыққа алады.',
          ],
        },
      ],
    },
  },

  // ─────────────────────────────────────────────────────────────────────────
  refund: {
    ru: {
      badge: 'Юридический документ',
      title:
        'Порядок возврата Услуг Держателем Карточки Предприятию и возврата денежных средств',
      backToHome: '← На главную',
      intro: [
        'Настоящий Порядок определяет условия возврата Услуг (доступа к Платформе «meelo», далее также — «Товар») и возврата денежных средств, оплаченных Держателем Карточки Предприятию, и является неотъемлемой частью Договора публичной оферты.',
        'Поскольку предметом договора является доступ к программному обеспечению (услуга в значении подпункта 6 статьи 1 Закона Республики Казахстан «О защите прав потребителей»), результат которой не имеет материального выражения, нормы о возврате непродовольственного товара надлежащего качества применяются с учётом характера Услуг.',
      ],
      sections: [
        {
          title: 'Возврат до начала использования',
          body: [
            'Если доступ к Платформе ещё не был активирован и Услуги фактически не оказывались, Держатель Карточки вправе отказаться от Услуг и потребовать возврата уплаченной суммы в полном объёме.',
          ],
        },
        {
          title: 'Возврат после начала использования',
          body: [
            'При отказе от Услуг после активации доступа возврату подлежат денежные средства за оплаченный, но неиспользованный период подписки, рассчитанные пропорционально. Стоимость фактически оказанных Услуг (за использованный период) возврату не подлежит.',
          ],
        },
        {
          title: 'Возврат при ненадлежащем качестве',
          body: [
            'Если Услуги оказаны с существенными недостатками и они не устранены Исполнителем в разумный срок, Держатель Карточки вправе потребовать возврата денежных средств за соответствующий период в полном объёме (см. также «Порядок замены»).',
          ],
        },
        {
          title: 'Порядок обращения',
          body: [
            `Для возврата Держатель Карточки направляет заявление на электронную почту ${SUPPORT_EMAIL} или иным указанным на сайте способом. В заявлении указываются: фамилия, имя, отчество (при наличии) Держателя Карточки; дата и сумма платежа; номер заказа/учётной записи; причина возврата; контактные данные.`,
          ],
        },
        {
          title: 'Способ возврата денег',
          body: [
            'Возврат денежных средств производится тем же способом и на ту же платёжную карту, с которой была произведена оплата. Возврат на другую карту или иным способом не производится (требование платёжных систем). Наличными денежные средства не возвращаются.',
          ],
        },
        {
          title: 'Срок возврата',
          body: [
            'Решение по заявлению принимается в течение 10 (десяти) рабочих дней. При положительном решении денежные средства перечисляются на платёжную карту в течение 14 (четырнадцати) календарных дней с даты согласования возврата. Срок фактического зачисления средств на счёт зависит от банка-эмитента карты и может составлять до 30 (тридцати) календарных дней.',
            'Удержанные банком и платёжными системами комиссии за проведение и возврат платежа (при их наличии) могут вычитаться из суммы возврата в случаях, предусмотренных правилами платёжных систем.',
            'Возврат денежных средств осуществляется в тенге в сумме, фактически оплаченной Держателем Карточки.',
          ],
        },
      ],
    },
    kk: {
      badge: 'Заңды құжат',
      title:
        'Карта ұстаушының Қызметтерді Кәсіпорынға қайтару және ақшалай қаражатты қайтару тәртібі',
      backToHome: '← Басты бетке',
      intro: [
        'Осы Тәртіп Карта ұстаушы Кәсіпорынға төлеген Қызметтерді («meelo» платформасына қол жеткізуді, бұдан әрі — «Тауар») қайтару және ақшалай қаражатты қайтару шарттарын айқындайды әрі Жария оферта шартының ажырамас бөлігі болып табылады.',
        'Шарттың мәні бағдарламалық қамтылымға қол жеткізу (Қазақстан Республикасының «Тұтынушылардың құқықтарын қорғау туралы» Заңының 1-бабы 6-тармақшасы мағынасындағы, нәтижесі материалдық көрініске ие емес қызмет) болғандықтан, тиісті сападағы азық-түлік емес тауарды қайтару нормалары Қызметтердің сипатын ескере отырып қолданылады.',
      ],
      sections: [
        {
          title: 'Пайдалануды бастағанға дейін қайтару',
          body: [
            'Егер Платформаға қол жеткізу әлі белсендірілмеген және Қызметтер іс жүзінде көрсетілмеген болса, Карта ұстаушы Қызметтерден бас тартуға және төленген соманы толық көлемде қайтаруды талап етуге құқылы.',
          ],
        },
        {
          title: 'Пайдалануды бастағаннан кейін қайтару',
          body: [
            'Қол жеткізу белсендірілгеннен кейін Қызметтерден бас тартқан кезде жазылымның төленген, бірақ пайдаланылмаған кезеңі үшін пропорционалды есептелген ақшалай қаражат қайтарылуға жатады. Іс жүзінде көрсетілген Қызметтердің (пайдаланылған кезең үшін) құны қайтарылмайды.',
          ],
        },
        {
          title: 'Тиісті емес сападағы кездегі қайтару',
          body: [
            'Егер Қызметтер елеулі кемшіліктермен көрсетілсе және оларды Орындаушы ақылға қонымды мерзімде жоймаса, Карта ұстаушы тиісті кезең үшін ақшалай қаражатты толық көлемде қайтаруды талап етуге құқылы («Ауыстыру тәртібін» де қараңыз).',
          ],
        },
        {
          title: 'Жүгіну тәртібі',
          body: [
            `Қайтару үшін Карта ұстаушы ${SUPPORT_EMAIL} электрондық поштасына немесе сайтта көрсетілген өзге тәсілмен өтініш жібереді. Өтініште көрсетіледі: Карта ұстаушының тегі, аты, әкесінің аты (бар болса); төлемнің күні мен сомасы; тапсырыс/есептік жазба нөмірі; қайтару себебі; байланыс деректері.`,
          ],
        },
        {
          title: 'Ақшаны қайтару тәсілі',
          body: [
            'Ақшалай қаражат төлем жасалған сол тәсілмен және сол төлем картасына қайтарылады. Басқа картаға немесе өзге тәсілмен қайтару жүргізілмейді (төлем жүйелерінің талабы). Ақшалай қаражат қолма-қол түрде қайтарылмайды.',
          ],
        },
        {
          title: 'Қайтару мерзімі',
          body: [
            'Өтініш бойынша шешім 10 (он) жұмыс күні ішінде қабылданады. Оң шешім қабылданған жағдайда ақшалай қаражат қайтару келісілген күннен бастап 14 (он төрт) күнтізбелік күн ішінде төлем картасына аударылады. Қаражаттың шотқа іс жүзінде есепке алыну мерзімі картаны шығарған банкке байланысты және 30 (отыз) күнтізбелік күнге дейін болуы мүмкін.',
            'Төлемді жүргізгені және қайтарғаны үшін банк пен төлем жүйелері ұстаған комиссиялар (бар болса) төлем жүйелерінің қағидаларында көзделген жағдайларда қайтару сомасынан шегерілуі мүмкін.',
            'Ақшалай қаражат теңгемен, Карта ұстаушы іс жүзінде төлеген сомада қайтарылады.',
          ],
        },
      ],
    },
  },

  // ─────────────────────────────────────────────────────────────────────────
  delivery: {
    ru: {
      badge: 'Юридический документ',
      title: 'Порядок и сроки предоставления доступа (поставки Услуг)',
      backToHome: '← На главную',
      intro: [
        'Услуги (доступ к Платформе «meelo», далее также — «Товар») предоставляются в электронном виде дистанционно, через сеть Интернет. Физическая доставка не осуществляется, материальный носитель не передаётся, расходы на доставку отсутствуют.',
      ],
      sections: [
        {
          title: 'Способ предоставления',
          body: [
            'Доступ к Платформе предоставляется через личный кабинет на сайте meelo.kz. Данные для доступа (логин, ссылка на активацию) направляются на адрес электронной почты, указанный Заказчиком при регистрации.',
          ],
        },
        {
          title: 'Срок предоставления',
          body: [
            'Доступ к Платформе активируется автоматически непосредственно после поступления оплаты. В случае необходимости ручной обработки заказа доступ предоставляется в срок не позднее 1 (одного) рабочего дня с момента подтверждения оплаты.',
          ],
        },
        {
          title: 'Срок действия доступа',
          body: [
            'Доступ к Платформе предоставляется на период оплаченной подписки (Тарифа) в соответствии с условиями, указанными на сайте meelo.kz.',
          ],
        },
        {
          title: 'Задержка предоставления',
          body: [
            `Если доступ не был предоставлен в указанный срок по причинам, зависящим от Исполнителя, Заказчик вправе обратиться в службу поддержки по адресу ${SUPPORT_EMAIL} или телефону ${SUPPORT_PHONE}. Исполнитель устраняет причину и предоставляет доступ в кратчайший срок либо по требованию Заказчика возвращает денежные средства в порядке, установленном «Порядком возврата».`,
            'Обязательство Исполнителя по предоставлению Услуг считается исполненным с момента предоставления Заказчику доступа к Платформе.',
          ],
        },
      ],
    },
    kk: {
      badge: 'Заңды құжат',
      title: 'Қол жеткізуді ұсыну (Қызметтерді жеткізу) тәртібі мен мерзімдері',
      backToHome: '← Басты бетке',
      intro: [
        'Қызметтер («meelo» платформасына қол жеткізу, бұдан әрі — «Тауар») электрондық түрде, қашықтан, Интернет желісі арқылы ұсынылады. Физикалық жеткізу жүзеге асырылмайды, материалдық жеткізгіш берілмейді, жеткізу шығыстары жоқ.',
      ],
      sections: [
        {
          title: 'Ұсыну тәсілі',
          body: [
            'Платформаға қол жеткізу meelo.kz сайтындағы жеке кабинет арқылы беріледі. Қол жеткізу деректері (логин, белсендіру сілтемесі) Тапсырыс беруші тіркелу кезінде көрсеткен электрондық пошта мекенжайына жіберіледі.',
          ],
        },
        {
          title: 'Ұсыну мерзімі',
          body: [
            'Платформаға қол жеткізу төлем түскеннен кейін бірден автоматты түрде белсендіріледі. Тапсырысты қолмен өңдеу қажет болған жағдайда қол жеткізу төлем расталған сәттен бастап 1 (бір) жұмыс күнінен кешіктірілмей беріледі.',
          ],
        },
        {
          title: 'Қол жеткізудің қолданылу мерзімі',
          body: [
            'Платформаға қол жеткізу meelo.kz сайтында көрсетілген талаптарға сәйкес төленген жазылым (Тариф) кезеңіне беріледі.',
          ],
        },
        {
          title: 'Ұсынудың кешіктірілуі',
          body: [
            `Егер қол жеткізу Орындаушыға байланысты себептермен көрсетілген мерзімде ұсынылмаса, Тапсырыс беруші ${SUPPORT_EMAIL} мекенжайы немесе ${SUPPORT_PHONE} телефоны бойынша қолдау қызметіне жүгінуге құқылы. Орындаушы себепті жояды және қол жеткізуді мүмкіндігінше қысқа мерзімде ұсынады не Тапсырыс берушінің талабы бойынша «Қайтару тәртібінде» белгіленген тәртіппен ақшалай қаражатты қайтарады.`,
            'Орындаушының Қызметтерді ұсыну жөніндегі міндеттемесі Тапсырыс берушіге Платформаға қол жеткізу ұсынылған сәттен бастап орындалды деп есептеледі.',
          ],
        },
      ],
    },
  },

  // ─────────────────────────────────────────────────────────────────────────
  replacement: {
    ru: {
      badge: 'Юридический документ',
      title:
        'Порядок замены Товара Предприятием Держателю Карточки в случае поставки некачественного и/или некомплектного Товара',
      backToHome: '← На главную',
      intro: [
        'Настоящий Порядок применяется в случаях, когда Услуги (доступ к Платформе «meelo», далее также — «Товар») предоставлены с недостатками — то есть Платформа недоступна, работает со сбоями или её функциональные возможности не соответствуют описанию, размещённому на сайте meelo.kz (некачественный и/или некомплектный «Товар»).',
      ],
      sections: [
        {
          title: 'Уведомление',
          body: [
            `При обнаружении недостатков Держатель Карточки (Заказчик) направляет уведомление в службу поддержки на электронную почту ${SUPPORT_EMAIL} или телефону ${SUPPORT_PHONE} с описанием недостатка, даты его обнаружения и данных учётной записи.`,
          ],
        },
        {
          title: 'Устранение недостатков',
          body: [
            'Исполнитель проверяет обращение и устраняет недостаток (восстанавливает доступ, исправляет сбой, предоставляет недостающий функционал) в разумный срок, как правило не позднее 3 (трёх) рабочих дней с момента обращения. Сложные технические недостатки устраняются в срок, дополнительно согласованный с Заказчиком.',
          ],
        },
        {
          title: 'Замена',
          body: [
            'Если недостаток не может быть устранён, Исполнитель по согласованию с Держателем Карточки предоставляет равноценный доступ (заменяет Тариф/функционал на аналогичный надлежащего качества) либо продлевает оплаченный период на срок, в течение которого Услуги были недоступны.',
          ],
        },
        {
          title: 'Возврат при невозможности замены',
          body: [
            'Если устранение недостатка или равноценная замена невозможны, Держатель Карточки вправе потребовать возврата денежных средств за соответствующий период в порядке, установленном «Порядком возврата».',
          ],
        },
        {
          title: 'Компенсация периода простоя',
          body: [
            'За период, в течение которого Платформа была недоступна по вине Исполнителя, оплаченный срок подписки продлевается на соответствующее количество дней без дополнительной оплаты.',
            'Настоящий Порядок применяется с учётом прав потребителей, предусмотренных Законом Республики Казахстан «О защите прав потребителей» от 4 мая 2010 года № 274-IV, на надлежащее качество товара (работы, услуги).',
          ],
        },
      ],
    },
    kk: {
      badge: 'Заңды құжат',
      title:
        'Сапасыз және/немесе жиынтықталмаған тауар жеткізілген жағдайда Кәсіпорынның Карта ұстаушыға тауарды ауыстыру тәртібі',
      backToHome: '← Басты бетке',
      intro: [
        'Осы Тәртіп Қызметтер («meelo» платформасына қол жеткізу, бұдан әрі — «Тауар») кемшіліктермен ұсынылған жағдайларда, яғни Платформа қолжетімсіз болса, іркіліспен жұмыс істесе немесе оның функционалдық мүмкіндіктері meelo.kz сайтында орналастырылған сипаттамаға сәйкес келмесе (сапасыз және/немесе жиынтықталмаған «Тауар») қолданылады.',
      ],
      sections: [
        {
          title: 'Хабарлама',
          body: [
            `Кемшіліктер анықталған кезде Карта ұстаушы (Тапсырыс беруші) ${SUPPORT_EMAIL} электрондық поштасына немесе ${SUPPORT_PHONE} телефонына кемшіліктің сипаттамасын, оның анықталған күнін және есептік жазба деректерін көрсете отырып хабарлама жібереді.`,
          ],
        },
        {
          title: 'Кемшіліктерді жою',
          body: [
            'Орындаушы жүгінуді тексереді және кемшілікті (қол жеткізуді қалпына келтіреді, іркілісті түзетеді, жетіспейтін функционалды ұсынады) ақылға қонымды мерзімде, әдетте жүгінген сәттен бастап 3 (үш) жұмыс күнінен кешіктірмей жояды. Күрделі техникалық кемшіліктер Тапсырыс берушімен қосымша келісілген мерзімде жойылады.',
          ],
        },
        {
          title: 'Ауыстыру',
          body: [
            'Егер кемшілік жойылмаса, Орындаушы Карта ұстаушымен келісім бойынша тең құнды қол жеткізуді ұсынады (Тарифті/функционалды тиісті сападағы баламалымен ауыстырады) не Қызметтер қолжетімсіз болған мерзімге төленген кезеңді ұзартады.',
          ],
        },
        {
          title: 'Ауыстыру мүмкін болмаған кездегі қайтару',
          body: [
            'Егер кемшілікті жою немесе тең құнды ауыстыру мүмкін болмаса, Карта ұстаушы тиісті кезең үшін ақшалай қаражатты «Қайтару тәртібінде» белгіленген тәртіппен қайтаруды талап етуге құқылы.',
          ],
        },
        {
          title: 'Тоқтап тұру кезеңін өтеу',
          body: [
            'Платформа Орындаушының кінәсінен қолжетімсіз болған кезең үшін төленген жазылым мерзімі тиісті күн санына қосымша ақысыз ұзартылады.',
            'Осы Тәртіп Қазақстан Республикасының «Тұтынушылардың құқықтарын қорғау туралы» 2010 жылғы 4 мамырдағы № 274-IV Заңында көзделген тұтынушылардың тауардың (жұмыстың, қызметтің) тиісті сапасына құқықтарын ескере отырып қолданылады.',
          ],
        },
      ],
    },
  },
}
